"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  index: "GDPR.01",
  title: "Δικαίωμα Πρόσβασης",
  description: "Έχετε το δικαίωμα να γνωρίζετε ποια προσωπικά σας δεδομένα επεξεργαζόμαστε, για ποιο σκοπό και ποιοι είναι οι αποδέκτες αυτών. Σας παρέχουμε πλήρη διαφάνεια και άμεση πρόσβαση στις πληροφορίες σας.",
  linkLabel: "Αίτημα πρόσβασης",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_access_portal.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_access_portal.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_access_portal.webp-wvc-sizes",
    alt: "Πύλη πρόσβασης δεδομένων χρήστη",
    label: "DATA ACCESS / 01"
  },
  imageRight: true
}, {
  index: "GDPR.02",
  title: "Δικαίωμα Διόρθωσης",
  description: "Εάν τα στοιχεία σας είναι ανακριβή ή ελλιπή, έχετε το δικαίωμα να ζητήσετε την άμεση διόρθωση ή συμπλήρωσή τους, διασφαλίζοντας ότι η εικόνα σας στα συστήματά μας είναι πάντα ενημερωμένη.",
  linkLabel: "Διόρθωση στοιχείων",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_correction_interface.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_correction_interface.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_correction_interface.png-wvc-sizes",
    alt: "Διεπαφή διόρθωσης προσωπικών στοιχείων",
    label: "DATA EDIT / 02"
  },
  imageRight: false
}, {
  index: "GDPR.03",
  title: "Δικαίωμα Διαγραφής",
  description: "Μπορείτε να ζητήσετε τη διαγραφή των προσωπικών σας δεδομένων όταν αυτά δεν είναι πλέον απαραίτητα για τους σκοπούς που συλλέχθηκαν ή εάν ανακαλέσετε τη συγκατάθεσή σας.",
  linkLabel: "Αίτημα διαγραφής",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_deletion_system.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_deletion_system.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_deletion_system.png-wvc-sizes",
    alt: "Σύστημα διαγραφής δεδομένων",
    label: "DATA PURGE / 03"
  },
  imageRight: true
}, {
  index: "GDPR.04",
  title: "Δικαίωμα στη Λήθη",
  description: "Σε συγκεκριμένες περιπτώσεις, έχετε το δικαίωμα να ζητήσετε την πλήρη και οριστική αφαίρεση των δεδομένων σας από κάθε ψηφιακό μας ίχνος, προστατεύοντας την ιδιωτικότητά σας στο μέγιστο βαθμό.",
  linkLabel: "Ενεργοποίηση λήθης",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_oblivion_protocol.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_oblivion_protocol.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/gdpr_oblivion_protocol.png-wvc-sizes",
    alt: "Πρωτόκολλο δικαιώματος στη λήθη",
    label: "OBLIVION / 04"
  },
  imageRight: false
}];
function CornerTicks() {
  return <>
      <span className="pointer-events-none absolute -top-px -left-px h-2.5 w-2.5 border-t border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -top-px -right-px h-2.5 w-2.5 border-t border-r border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -left-px h-2.5 w-2.5 border-b border-l border-[var(--primary)]" aria-hidden="true" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-2.5 w-2.5 border-b border-r border-[var(--primary)]" aria-hidden="true" />
    </>;
}
function VioletBeam({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`} aria-hidden="true">
      <div className="h-px flex-1 bg-border" />
      <div className="h-1.5 w-1.5 rounded-md bg-[var(--primary)]" />
    </div>;
}
export default function Gdpr() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1888" id="δικαιώματα-του-χρήστη-gdpr" className="relative bg-background text-foreground py-28 md:py-36 overflow-hidden">
      <div ref={header.ref} className={`relative mx-auto max-w-[880px] px-5 md:px-8 text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+60px)] font-default font-semibold text-[12rem] md:text-[22rem] leading-none tracking-tighter select-none" style={{
        WebkitTextStroke: "1px var(--border)",
        color: "transparent"
      }}>
          02
        </span>

        <div className="relative z-10">
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.12em] text-muted-foreground mb-6">
            GDPR & Προστασία
          </p>
          <h2 className="font-default font-semibold text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.02] tracking-tight">
            Τα δικαιώματά σας,{" "}
            <span className="italic" style={{
            WebkitTextStroke: "1px var(--primary)",
            color: "transparent"
          }}>
              απόλυτα
            </span>{" "}
            σεβαστά.
          </h2>
          <p className="mt-6 font-default text-lg text-muted-foreground max-w-2xl mx-auto">
            Σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (GDPR), διασφαλίζουμε τον πλήρη έλεγχο των πληροφοριών σας μέσα από τέσσερις βασικούς πυλώνες.
          </p>
          <VioletBeam className="mt-8 mx-auto max-w-xs" />
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-5 md:px-8 mt-20 md:mt-28">
        {features.map((feature, i) => {
        const row = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.15
        });
        const imageOnRight = feature.imageRight;
        return <div key={i} data-index={i}>
              <div ref={row.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center transition-all duration-700 ease-out ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <div className={`lg:col-span-7 ${imageOnRight ? "lg:order-1" : "lg:order-2"}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--primary)] mb-4">
                    {feature.index}
                  </p>
                  <h3 className="font-default font-medium text-[clamp(1.5rem,2vw,2rem)] leading-[1.15] tracking-tight mb-5">
                    {feature.title}
                  </h3>
                  <p className="font-default text-[clamp(1.0625rem,1.25vw,1.25rem)] leading-[1.6] text-muted-foreground max-w-xl mb-8">
                    {feature.description}
                  </p>
                  <Link to="#" className="group inline-flex items-center gap-2 text-foreground hover:text-[var(--primary)] transition-colors duration-200">
                    <span className="font-default font-medium text-sm md:text-base">
                      {feature.linkLabel}
                    </span>
                    <ArrowRight className="size-4 text-[var(--primary)] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>

                <div className={`lg:col-span-5 relative ${imageOnRight ? "lg:order-2" : "lg:order-1"}`}>
                  <div aria-hidden="true" className={`pointer-events-none absolute -z-10 h-[420px] w-[420px] rounded-lg blur-3xl opacity-50 md:opacity-100 ${imageOnRight ? "-bottom-20 -left-20" : "-bottom-20 -right-20"}`} style={{
                background: "radial-gradient(closest-side, hsl(77 100% 45% / 0.15), transparent 70%)"
              }} />

                  <div className="group relative">
                    <div className="relative border border-border bg-card overflow-hidden rounded-md transition-colors duration-300 group-hover:border-[var(--primary)]">
                      <CornerTicks />

                      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[hsl(229_75%_5%)]">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-md bg-[hsl(229_10%_20%)]" />
                          <span className="h-2 w-2 rounded-md bg-[hsl(229_10%_20%)]" />
                          <span className="h-2 w-2 rounded-md bg-[hsl(229_10%_20%)]" />
                        </div>
                        <p className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground uppercase">
                          {feature.image.label}
                        </p>
                        <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--primary)] uppercase">
                          SECURE
                        </span>
                      </div>

                      <div className="overflow-hidden">
                        <img src={feature.image.src} data-wvc-srcset={feature.image.srcset} data-wvc-sizes={feature.image.sizes} alt={feature.image.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {i < features.length - 1 && <VioletBeam className="my-16 md:my-24" />}
            </div>;
      })}
      </div>
    </section>;
}